import { useEffect } from 'react';
import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';

import { AppShell } from '@/components/shell/app-shell';
import { soleTraderLanding } from '@/app/home-landing';
import { reachableWithoutBuildAccess, soleTraderProjectIds } from '@/app/player';
import { NotEnabledScreen } from '@/features/auth/not-enabled';
import { ViewAsEndedScreen } from '@/features/view-as/view-as-ended';
import { StepUpResume } from '@/features/step-up/step-up';
import { bootSession, useSession } from '@/lib/auth/session';
import { getViewAs, useViewAs } from '@/lib/auth/view-as';
import { syncEngine } from '@/lib/sync';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    const session = await bootSession();
    // A staff "view as" session that ended shows its own screen, never a sign-in.
    if (getViewAs().ended) return;
    if (session.status === 'signed_out') {
      throw redirect({ to: '/signin', search: { return_to: location.href } });
    }
  },
  component: AppLayout,
});

function SyncLifecycle() {
  useEffect(() => syncEngine.start(), []);
  return null;
}

function AppLayout() {
  const { status, me } = useSession();
  const viewAs = useViewAs();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (!viewAs.active && viewAs.ended) return <ViewAsEndedScreen />;
  if (status === 'signed_out') return <Navigate to="/signin" />;
  if (me && !me.entitlements.build_access) {
    // Workers use their own record for free: /me (and their notifications,
    // which link into /me/work/…) are identity-only on the server, and so is
    // Settings › Your data (download or erase what Build holds about you).
    // A sole trader also works their own sole-trader projects (the server's
    // exemption): those projects, Projects, Sync Center and Messages — with
    // the sync engine running. Everything else needs KorOS Build access.
    const soleTrader = soleTraderProjectIds(me).length > 0;
    if (!reachableWithoutBuildAccess(me, pathname)) {
      const landing = pathname === '/home' || pathname === '/' ? soleTraderLanding(me) : null;
      if (landing) return <Navigate {...(landing as { to: '/projects' })} replace />;
      return <NotEnabledScreen />;
    }
    return (
      <AppShell>
        {soleTrader && <SyncLifecycle />}
        {soleTrader && <StepUpResume />}
        <Outlet />
      </AppShell>
    );
  }
  return (
    <AppShell>
      <SyncLifecycle />
      <StepUpResume />
      <Outlet />
    </AppShell>
  );
}
